import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../auth/contexts/AuthContext';
import { collection, doc, onSnapshot, query, where, getDoc, getDocs, setDoc } from 'firebase/firestore';
import { auth, db } from '../../../shared/storage/firebase';
import { Squad } from '../../../types';
import GameLaunchModal from './GameLaunchModal';
import { GAME_REGISTRY } from '../models/GameRegistry';
import { gameAPI } from '../services/GameAPI';
import { useLocation, useNavigate } from 'react-router-dom';

const StudentGameLaunchListener: React.FC = () => {
  const { userData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState<{
    squadId: string;
    gameId: string;
    sessionId: string;
    version: number;
  } | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [gameAchievements, setGameAchievements] = useState<any[]>([]);
  const [userAchievements, setUserAchievements] = useState<any[]>([]);
  const [hasEverSeenLaunchModal, setHasEverSeenLaunchModal] = useState<boolean | null>(null);
  const lastAutoLaunchSessionIdRef = useRef<string | null>(null);

  const studentId = userData?.id || '';
  const shouldListen = useMemo(() => userData?.role === 'student' && !!studentId, [userData?.role, studentId]);

  // Load global "seen once" flag (Firestore + localStorage fallback).
  useEffect(() => {
    if (!shouldListen || !studentId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const localKey = `ogp:gameLaunchModalSeen:${studentId}`;
        const localSeen = window.localStorage?.getItem(localKey) === 'true';
        if (localSeen) {
          if (!cancelled) setHasEverSeenLaunchModal(true);
          return;
        }

        const userRef = doc(db, 'users', studentId);
        const snap = await getDoc(userRef);
        const seen = !!snap.data()?.hasSeenGameLaunchModal;
        if (!cancelled) setHasEverSeenLaunchModal(seen);
      } catch (e) {
        // If we can't read it, default to "not seen" so the student isn't blocked.
        if (!cancelled) setHasEverSeenLaunchModal(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [shouldListen, studentId]);

  const markLaunchModalSeenOnce = useCallback(async () => {
    if (!studentId) return;
    // 1) localStorage backup (covers cases where Firestore write is blocked/flaky)
    try {
      const localKey = `ogp:gameLaunchModalSeen:${studentId}`;
      window.localStorage?.setItem(localKey, 'true');
    } catch {
      // ignore
    }

    // 2) Firestore global flag (cross-device)
    // NOTE: students often run under anonymous auth, and rules may block writes to users/{studentId}.
    // In that case, localStorage is the source of truth for the "ever seen" requirement.
    try {
      const currentUser = auth.currentUser;
      const canWriteUsersDoc = !!currentUser && !currentUser.isAnonymous && currentUser.uid === studentId;
      if (canWriteUsersDoc) {
        const userRef = doc(db, 'users', studentId);
        await setDoc(
          userRef,
          { hasSeenGameLaunchModal: true, hasSeenGameLaunchModalAt: new Date() },
          { merge: true }
        );
      }
    } catch {
      // ignore firestore write failures (localStorage already set)
    } finally {
      setHasEverSeenLaunchModal(true);
    }
  }, [studentId]);

  useEffect(() => {
    // Don't subscribe until we've hydrated the "has ever seen" flag.
    // Otherwise a refresh can show the modal again before the flag loads.
    if (!shouldListen || hasEverSeenLaunchModal === null) return;

    const squadsRef = collection(db, 'squads');
    const q = query(squadsRef, where('studentIds', 'array-contains', studentId));

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      type FoundLaunch = { squadId: string; gameId: string; sessionId: string; version: number };

      // Important: compute `found` without assigning inside a callback so TS can narrow types correctly.
      const found: FoundLaunch | null = snapshot.docs.reduce<FoundLaunch | null>((acc, s) => {
        if (acc) return acc;
        const data = { id: s.id, ...s.data() } as unknown as Squad & any;
        const als = (data as any).activeLaunchSession;
        if (!als) return null;
        const gameId = String(als.gameId || '');
        const sessionId = String(als.sessionId || '');
        const version = Number(als.version || 0);
        if (!gameId || !sessionId) return null;
        return { squadId: data.id, gameId, sessionId, version };
      }, null);

      if (!found) {
        setIsOpen(false);
        setActive(null);
        return;
      }

      // If the student has EVER seen the launch modal once, never show it again.
      // Requirement (per product): after the first ever popup, do not popup again and do not auto-route.
      if (hasEverSeenLaunchModal === true) {
        setActive(found);
        setIsOpen(false);
        return;
      }

      // Check if student has already dismissed this version
      try {
        const f = found;
        const dismissalRef = doc(db, 'users', studentId, 'gameLaunchDismissals', f.sessionId);
        const dismissalSnap = await getDoc(dismissalRef);
        
        if (dismissalSnap.exists()) {
          const dismissalData = dismissalSnap.data();
          if (dismissalData?.version >= f.version) {
            // Already dismissed this version
            setIsOpen(false);
            setActive(f);
            return;
          }
        }

        // Show the modal
        // Requirement: once it has ever popped up once, it should never pop up again.
        // So we mark "seen" immediately when we decide to show it (not on close).
        await markLaunchModalSeenOnce();
        setActive(f);
        setIsOpen(true);
      } catch (e) {
        // On error, default to opening to avoid blocking students
        await markLaunchModalSeenOnce();
        setActive(found as any);
        setIsOpen(true);
      }
    }, (error) => {
      console.error('StudentGameLaunchListener squad query error', error);
    });

    return () => unsubscribe();
  }, [shouldListen, studentId, hasEverSeenLaunchModal, markLaunchModalSeenOnce]);

  // Load achievements when modal opens
  useEffect(() => {
    if (!isOpen || !active?.gameId || !studentId) return;

    const loadAchievements = async () => {
      try {
        const achievements = await gameAPI.getGameAchievementDefinitions(active.gameId);
        setGameAchievements(achievements);

        // Load user's achievements for this game
        const userAchievementsRef = collection(db, 'users', studentId, 'achievements');
        const userAchievementsSnapshot = await getDocs(userAchievementsRef);
        const allUserAchievements = userAchievementsSnapshot.docs
          .map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              userId: studentId,
              achievementId: data.achievementId,
              unlockedAt: data.unlockedAt?.toDate() || new Date(),
              gameId: data.gameId
            };
          })
          .filter(a => a.gameId === active.gameId);
          
        setUserAchievements(allUserAchievements);
      } catch (error) {
        console.error('Error loading achievements for game launch:', error);
      }
    };

    loadAchievements();
  }, [isOpen, active?.gameId, studentId]);

  const handlePlay = async () => {
    if (!active) return;

    // Important: persist dismissal BEFORE navigating.
    // Otherwise the component can unmount during route change and drop the write,
    // causing the launch modal to reappear throughout class.
    await handleClose();

    // Navigate to game
    navigate(`/play/${active.gameId}`);
  };

  const handleClose = async () => {
    if (!active || !studentId) return;

    // Record dismissal
    try {
      const dismissalRef = doc(db, 'users', studentId, 'gameLaunchDismissals', active.sessionId);
      await setDoc(dismissalRef, {
        sessionId: active.sessionId,
        gameId: active.gameId,
        squadId: active.squadId,
        version: active.version,
        dismissedAt: new Date(),
      });
    } catch (e) {
      console.error('Failed to record game launch dismissal', e);
    }

    setIsOpen(false);
  };

  if (!isOpen || !active || !studentId) return null;

  const game = GAME_REGISTRY[active.gameId];
  if (!game) return null;

  return (
    <GameLaunchModal
      game={game}
      onPlay={handlePlay}
      onClose={handleClose}
      achievements={gameAchievements}
      userAchievements={userAchievements}
    />
  );
};

export default StudentGameLaunchListener;
